/**
 * Admin help with someone's password (owner, 2026-10-11): "Send reset link" emails them a link to choose a new one;
 * "Set new password" sets it now (they are logged out elsewhere and told by email). Shown only with the permission
 * `accounts.reset_password` (Super Admins always). Used on a vendor's team members and on customer pages.
 */

import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { adminUsersPasswordReset, adminUsersPasswordSet, isApiError } from "@motolynk/api-client";
import { useTranslation } from "@motolynk/i18n";
import {
  Alert,
  Button,
  ConfirmDialog,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FormField,
  Input,
  toast,
} from "@motolynk/ui";

import { applyServerErrors } from "./forms";
import { usePermissions } from "./session";

export type PasswordPanel = "admin" | "vendor" | "store"; // pragma: allowlist secret

export function PasswordHelp({
  userUid,
  email,
  panel,
}: {
  userUid: string;
  email: string;
  /** Which login page the emails point to. */
  panel: PasswordPanel;
}) {
  const { t } = useTranslation("common");
  const { can } = usePermissions();
  const [confirm, setConfirm] = useState(false);
  const [setting, setSetting] = useState(false);
  const reset = useMutation({
    mutationFn: () => adminUsersPasswordReset(userUid, { panel }),
    onSuccess: () => toast.success(t("passwordHelp.sent", { email })),
    onError: (error) => toast.error(isApiError(error) ? error.message : t("passwordHelp.failed")),
  });
  if (!can("accounts.reset_password")) return null;
  return (
    <span className="inline-flex flex-wrap gap-2">
      <Button
        variant="outline"
        size="sm"
        onClick={() => setConfirm(true)}
        loading={reset.isPending}
      >
        {t("passwordHelp.sendLink")}
      </Button>
      <Button variant="outline" size="sm" onClick={() => setSetting(true)}>
        {t("passwordHelp.setNew")}
      </Button>
      <ConfirmDialog
        open={confirm}
        onOpenChange={setConfirm}
        title={t("passwordHelp.sendLink")}
        description={t("passwordHelp.sendLinkConfirm", { email })}
        confirmLabel={t("passwordHelp.sendLink")}
        cancelLabel={t("actions.cancel")}
        onConfirm={() => reset.mutate()}
      />
      {setting && (
        <SetPasswordDialog
          userUid={userUid}
          email={email}
          panel={panel}
          onClose={() => setSetting(false)}
        />
      )}
    </span>
  );
}

function SetPasswordDialog({
  userUid,
  email,
  panel,
  onClose,
}: {
  userUid: string;
  email: string;
  panel: PasswordPanel;
  onClose: () => void;
}) {
  const { t } = useTranslation("common");
  const schema = z
    .object({
      password: z.string().min(8, t("passwordHelp.tooShort")),
      confirm: z.string(),
    })
    .refine((v) => v.password === v.confirm, {
      path: ["confirm"],
      message: t("passwordHelp.mismatch"),
    });
  type Values = z.infer<typeof schema>;
  const form = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: { password: "", confirm: "" },
  });
  const [formError, setFormError] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: (values: Values) =>
      adminUsersPasswordSet(userUid, { password: values.password, panel }),
    onSuccess: () => {
      toast.success(t("passwordHelp.changed", { email }));
      onClose();
    },
    onError: (error) => setFormError(applyServerErrors(error, form.setError, ["password"])),
  });
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent closeLabel={t("actions.close")}>
        <DialogHeader>
          <DialogTitle>{t("passwordHelp.setNew")}</DialogTitle>
          <DialogDescription>{t("passwordHelp.setNewBody", { email })}</DialogDescription>
        </DialogHeader>
        <form
          noValidate
          className="space-y-4"
          onSubmit={form.handleSubmit((values) => {
            setFormError(null);
            save.mutate(values);
          })}
        >
          <FormField
            label={t("passwordHelp.password")}
            error={form.formState.errors.password?.message}
            required
          >
            <Input type="password" autoComplete="new-password" {...form.register("password")} />
          </FormField>
          <FormField
            label={t("passwordHelp.confirm")}
            error={form.formState.errors.confirm?.message}
            required
          >
            <Input type="password" autoComplete="new-password" {...form.register("confirm")} />
          </FormField>
          {formError && <Alert tone="danger">{formError}</Alert>}
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose}>
              {t("actions.cancel")}
            </Button>
            <Button type="submit" loading={save.isPending}>
              {t("passwordHelp.setNew")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
